/**
 * Centrifugo Realtime Provider
 *
 * Self-hosted Centrifugo implementation of the RealtimeProvider interface.
 * Uses server-side subscriptions via JWT channels claim.
 * Connects using a JWT token and maps Centrifugo publications to DOM CustomEvents.
 */

import { Centrifuge } from 'centrifuge'
import { environment } from '@/shared/env'
import { getCentrifugoToken } from '@/shared/centrifugoToken'
import { RealtimeProvider, RealtimeInitParams } from './types'
import {
  LIVECHAT_CHANNEL_EVENTS,
  BROADCAST_CHANNEL_EVENTS,
  WHATSAPP_CHANNEL_EVENTS,
  NOTIFICATION_CHANNEL_EVENTS,
  dispatchDomEvent,
  resolveForceLogoutChannelName,
  shouldHandleForceLogout,
} from './utils'

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

interface CentrifugoPublicationData {
  event?: string
  data?: unknown
  [key: string]: unknown
}

interface CentrifugoProviderState {
  centrifuge: Centrifuge | null
  livechatChannel: string | null
  broadcastChannel: string | null
  whatsappChannel: string | null
  notificationChannel: string | null
  forceLogoutChannel: string | null
}

// ---------------------------------------------------------------------------
// Centrifugo Provider Implementation
// ---------------------------------------------------------------------------

export class CentrifugoProvider implements RealtimeProvider {
  private state: CentrifugoProviderState = {
    centrifuge: null,
    livechatChannel: null,
    broadcastChannel: null,
    whatsappChannel: null,
    notificationChannel: null,
    forceLogoutChannel: null,
  }

  private groupId?: string
  private refreshToken?: string

  /**
   * Route publications to the correct event map based on channel name.
   * Server-side subscriptions via JWT channels claim automatically deliver publications
   * to this single top-level handler without explicit .subscribe() calls per channel.
   */
  private handlePublication(ctx: { channel: string; data: CentrifugoPublicationData }): void {
    const channel = ctx.channel
    const payload = ctx?.data ?? {}
    const evt = payload.event
    const inner = payload.data !== undefined ? payload.data : payload

    // Only log in debug mode to avoid production spam
    if (environment('VUE_APP_DEBUG_REALTIME') === 'true') {
      console.info(`[Centrifugo] PUBLICATION on channel "${channel}":`, JSON.stringify(payload))
    }

    // Route by channel name to the appropriate event map
    if (channel === this.state.livechatChannel) {
      // Handle group-scoped events (my-event-${group_id})
      if (this.groupId && evt === `my-event-${this.groupId}`) {
        dispatchDomEvent('new-message', inner)
        return
      }
      // Handle other livechat events
      if (evt && LIVECHAT_CHANNEL_EVENTS[evt]) {
        dispatchDomEvent(LIVECHAT_CHANNEL_EVENTS[evt], inner)
      }
    } else if (channel === this.state.broadcastChannel) {
      if (evt && BROADCAST_CHANNEL_EVENTS[evt]) {
        dispatchDomEvent(BROADCAST_CHANNEL_EVENTS[evt], inner)
      }
    } else if (channel === this.state.whatsappChannel) {
      if (evt && WHATSAPP_CHANNEL_EVENTS[evt]) {
        dispatchDomEvent(WHATSAPP_CHANNEL_EVENTS[evt], inner)
      }
    } else if (channel === this.state.notificationChannel) {
      if (evt && NOTIFICATION_CHANNEL_EVENTS[evt]) {
        dispatchDomEvent(NOTIFICATION_CHANNEL_EVENTS[evt], inner)
      }
    } else if (channel === this.state.forceLogoutChannel) {
      if (evt === 'force_logout') {
        if (!shouldHandleForceLogout(inner, this.refreshToken)) {
          console.debug('[Centrifugo] Skipping force_logout: validation failed')
          return
        }
        dispatchDomEvent('force-logout', inner)
      }
    } else {
      // Warn about unrecognized channels
      console.warn(`[Centrifugo] Unknown channel received: "${channel}"`)
    }
  }

  initRealtime({ referallshopName, group_id, authkey, token, refreshToken }: RealtimeInitParams): void {
    // Idempotency guard — prevent re-init on navigation
    if (this.state.centrifuge) {
      console.debug('[Centrifugo] Already initialized, skipping re-init')
      return
    }

    if (!referallshopName) {
      console.warn('[CentrifugoProvider] Cannot initialize: referallshopName is required')
      return
    }

    const url = environment('VUE_APP_CENTRIFUGO_URL')
    if (!url) {
      console.warn('[CentrifugoProvider] VUE_APP_CENTRIFUGO_URL is not set')
      return
    }

    // Store groupId for group-scoped event routing
    this.groupId = group_id
    // Store refreshToken for force-logout validation
    this.refreshToken = refreshToken

    const centrifuge = new Centrifuge(url, {
      // getToken is called on connect and on token refresh.
      // Returning '' is a valid "no token" result and does not force disconnect.
      getToken: async () => {
        try {
          return await getCentrifugoToken()
        } catch (error) {
          console.error('[Centrifugo] Failed to fetch token:', error)
          throw error
        }
      },
    })

    centrifuge.on('connecting', () => console.info('[Centrifugo] Connecting...'))
    centrifuge.on('connected', (ctx) => console.info('[Centrifugo] Connected', ctx))
    centrifuge.on('disconnected', (ctx) => console.info('[Centrifugo] Disconnected', ctx))
    centrifuge.on('error', (ctx) => console.error('[Centrifugo] Error', ctx))

    // Derive shopname from referallShopName (e.g., "acme-corp")
    const shopname = referallshopName

    // Store channel names for routing
    this.state.livechatChannel = `${shopname}-livechat`
    this.state.broadcastChannel = `${shopname}-broadcast`
    this.state.whatsappChannel = `${shopname}-whatsapp`
    this.state.notificationChannel = authkey || null
    this.state.forceLogoutChannel = token ? resolveForceLogoutChannelName(token) : null

    // Single top-level publication handler for all server-side subscribed channels
    // Per Centrifugo docs: "Listen for publications without any usage of subscription objects"
    centrifuge.on('publication', (ctx) => {
      this.handlePublication(ctx)
    })

    this.state.centrifuge = centrifuge
    centrifuge.connect()

    console.info(
      '[Centrifugo] Initialized with server-side subscriptions via JWT channels claim:',
      {
        livechat: this.state.livechatChannel,
        broadcast: this.state.broadcastChannel,
        whatsapp: this.state.whatsappChannel,
        notification: this.state.notificationChannel,
        forceLogout: this.state.forceLogoutChannel,
        groupId: this.groupId,
      },
    )
  }

  disconnectRealtime(): void {
    if (!this.state.centrifuge) {
      console.debug('[Centrifugo] Already disconnected, skipping')
      return
    }

    console.info('[Centrifugo] Disconnecting...')
    this.state.centrifuge.disconnect()
    this.state.centrifuge = null
    this.state.livechatChannel = null
    this.state.broadcastChannel = null
    this.state.whatsappChannel = null
    this.state.notificationChannel = null
    this.state.forceLogoutChannel = null
    this.refreshToken = undefined

    console.info('[Centrifugo] Disconnection complete')
  }

  isConnected(): boolean {
    return this.state.centrifuge !== null
  }
}
